import { ResponsiveValue } from '@shopify/restyle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextProps } from 'react-native';
import {
  Config,
  isTriggerConfig,
  parseValue,
  Part,
} from 'react-native-controlled-mentions';

import CachedImage from '@components/CachedImage/CachedImage';
import { EVERYONE_ID } from '@components/Chat/MentionSuggestions';
import Highlighter from '@components/HighlightWords';
import { Text } from '@components/Restyle';
import Box from '@components/Restyle/Box';
import Icon from '@components/shared/Icon/Icon';
import { Attachment, Document, Maybe } from '@graphql/generated';
import { useAlert } from '@hooks/useAlert';
import { useChatMentionPress } from '@hooks/useChatMentionPress';
import theme, { Theme } from '@themes/theme';
import {
  copyLinkUrl,
  openLinkUrl,
  splitTextWithLinks,
} from '@utils/linkifyText';
import { generateTagCollections } from '@utils/tagCollectionService';

import TagsCollection from './TagsCollection';

type ReplyMessagePartsType = {
  value: string;
  configs: Config[];
  styleLinks?: boolean;
  filterVal?: string;
  textColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;

  // if rendering mention from a non-chat detail screen
  navigateToChatOnMentionPress?: boolean;
  attachments?: Maybe<Array<Attachment>>;
} & TextProps;

const ReplyMessageParts: React.FC<ReplyMessagePartsType> = ({
  value,
  configs,
  styleLinks,
  filterVal = '',
  textColor = 'textSecondary',
  attachments,
  navigateToChatOnMentionPress,
  ...rest
}) => {
  const { parts } = parseValue(value, configs);
  const { showAlert } = useAlert();
  const { t } = useTranslation('models');

  const { onMentionPress } = useChatMentionPress(navigateToChatOnMentionPress);

  const highlightText = (body: string) => {
    if (!filterVal) {
      return body;
    }
    return (
      <Highlighter
        autoEscape
        highlightStyle={{
          backgroundColor: theme.colors.yellowBright,
        }}
        searchWords={filterVal.split(' ')}
        textToHighlight={body}
      />
    );
  };

  // Splits a plain-text part into URL and non-URL <Text> segments instead of
  // wrapping the whole render tree in react-native-hyperlink, which always
  // rendered a View wrapper around its children -- risking the same
  // Text-layout break as MessageParts.tsx hit (TA-10489, TA-10697).
  const linkifyReplyPart = (text: string, index: number) => {
    const fallbackNode = (
      <Text
        variant='bodySecondary'
        color={textColor}
        key={index}
        style={{
          borderWidth: 0,
          borderColor: 'transparent',
        }}>
        {highlightText(text)}
      </Text>
    );

    if (!styleLinks) return fallbackNode;

    const segments = splitTextWithLinks(
      text,
      `${index}`,
      (segment, key) => (
        <React.Fragment key={key}>{highlightText(segment)}</React.Fragment>
      ),
      (url, displayText, key) => (
        <Text
          key={key}
          variant='bodySecondary'
          style={{ color: theme.colors.blue }}
          onPress={() => openLinkUrl(url)}
          onLongPress={() => copyLinkUrl(url, showAlert)}>
          {highlightText(displayText)}
        </Text>
      )
    );

    if (!segments) return fallbackNode;

    return (
      <Text
        variant='bodySecondary'
        color={textColor}
        key={index}
        style={{
          borderWidth: 0,
          borderColor: 'transparent',
        }}>
        {segments}
      </Text>
    );
  };

  const renderPart = (part: Part, index: number) => {
    // Mention type part
    if (part.config && isTriggerConfig(part.config)) {
      const { id: userId } = part.data || { userId: '' };
      const disablePress = !userId || userId === EVERYONE_ID;

      const onPress = () => {
        if (userId) onMentionPress(userId);
      };

      return (
        <Text
          key={index}
          style={!filterVal && part.config.textStyle}
          variant='labelSmall'
          color={textColor}
          onPress={disablePress ? undefined : onPress}>
          {highlightText(part.text)}
        </Text>
      );
    }

    return linkifyReplyPart(part.text, index);
  };
  const tagsByAuthorCollections = generateTagCollections(attachments || []);
  const isDocumentType = (item: Attachment): item is Document =>
    item.__typename === 'Document';

  const documentAttachments: Document[] =
    attachments === undefined
      ? []
      : (attachments?.filter((item) => {
          return isDocumentType(item);
        }) as Document[]);

  const audioAttachments = documentAttachments.filter((item) => item.isAudio);
  const imageAttachments = documentAttachments.filter((item) => item.isImage);
  const fileAttachments = documentAttachments.filter(
    (item) => !item.isImage && !item.isAudio
  );

  const hasFile =
    audioAttachments.length > 0 ||
    imageAttachments.length > 0 ||
    fileAttachments.length > 0;
  const hasProjectTag = !hasFile && tagsByAuthorCollections.length > 0;
  const hasText = value.length > 0;

  const renderTagsCollections = () => {
    if (tagsByAuthorCollections.length > 0) {
      const tagsCollection = tagsByAuthorCollections.flat();

      return (
        <Box flexDirection='column' flex={1}>
          {tagsCollection.length > 0 &&
            tagsCollection.map((tags) => (
              <TagsCollection
                key={tags?.project?.id}
                showAuthor={false}
                borderless
                tagsCollection={{
                  tasks: tags.tasks || [],
                  project: tags.project || {},
                  author: tags.author || {},
                }}
                filterVal=''
              />
            ))}
        </Box>
      );
    }
    return null; // or any other fallback if there are no tags
  };

  const renderParts = (
    <Box flex={1} flexDirection='row' alignItems='center' marginTop='xs'>
      {audioAttachments.length > 0 && (
        <Box flexDirection='row' alignItems='center'>
          <Icon name='Mic' color='grey05' variant='s' />
          {!hasText && (
            <Text
              variant='bodySecondary'
              color='textSecondary'
              numberOfLines={1}
              marginLeft='xs'>
              {t('chat.labels.audio_1')}
            </Text>
          )}
        </Box>
      )}
      {imageAttachments.length > 0 && (
        <Box flexDirection='row' alignItems='center'>
          <CachedImage
            imageClientId={imageAttachments[0].clientId ?? ''}
            image={imageAttachments[0].file}
            width={34}
            height={34}
            placeholderContentFlex={0}
            isRemoveable={false}
            borderRadius={theme.spacing.xxs}
          />
          {!hasText && (
            <Text
              variant='bodySecondary'
              color='textSecondary'
              numberOfLines={1}
              marginLeft='xs'>
              {imageAttachments.length === 1
                ? t('chat.labels.photo_one')
                : imageAttachments.length.toString() +
                  ' ' +
                  t('chat.labels.photo_other')}
            </Text>
          )}
        </Box>
      )}
      {fileAttachments.length > 0 && (
        <Box flexDirection='row' alignItems='center'>
          <Icon
            key={fileAttachments[0].id}
            fileType={fileAttachments[0].contentType || 'unknown'}
            color='black'
            variant='m'
          />
          {!hasText && (
            <Text
              variant='bodySecondary'
              color='textSecondary'
              numberOfLines={1}
              marginLeft='xs'>
              {fileAttachments.length === 1
                ? fileAttachments[0].name
                : fileAttachments.length.toString() +
                  ' ' +
                  t('chat.labels.file_other')}
            </Text>
          )}
        </Box>
      )}
      <Box flexDirection='column' flex={1}>
        {renderTagsCollections()}
        {hasText && (
          <Box flex={1}>
            <Text
              marginLeft={hasFile || hasProjectTag ? 'xs' : 'none'}
              {...rest}>
              {parts.map(renderPart)}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );

  return renderParts;
};

export default ReplyMessageParts;
